import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errorText } from "@/lib/api";
import { ITEM_FIELDS, missingFields, txt } from "@/lib/format";
import { useAdminLang } from "./context";
import { SortableList, SortableRow, moveId } from "./Sortable";
import { ItemRow } from "./ItemRow";
import ItemEditor from "./ItemEditor";

const FilterSelect = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-full sm:w-auto sm:min-w-[160px]" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([v, label]) => <SelectItem key={v} value={v} data-testid={`${testid}-${v}`}>{label}</SelectItem>)}</SelectContent>
  </Select>
);

function matches(i, f, lang) {
  if (f.cat !== "all" && i.category_id !== f.cat) return false;
  if (f.vis !== "all" && i.is_visible !== (f.vis === "visible")) return false;
  if (f.avail !== "all" && i.is_available !== (f.avail === "available")) return false;
  if (f.tr !== "all") {
    const m = missingFields(i, ITEM_FIELDS);
    if (f.tr === "missing" && !m.en.length && !m.ar.length) return false;
    if (f.tr === "missing_ar" && !m.ar.length) return false;
    if (f.tr === "missing_en" && !m.en.length) return false;
  }
  const q = f.q.trim().toLocaleLowerCase();
  return !q || `${i.title_en} ${i.title_ar} ${i.description_en} ${i.description_ar}`.toLocaleLowerCase().includes(q);
}

export default function Items() {
  const { t, lang } = useAdminLang();
  const { refresh } = useOutletContext();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [currency, setCurrency] = useState("SAR");
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ q: "", cat: "all", vis: "all", avail: "all", tr: params.get("tr") === "missing" ? "missing" : "all" });
  const [editor, setEditor] = useState(params.get("new") ? { key: Date.now(), item: null } : null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([api.get("/admin/items"), api.get("/admin/categories"), api.get("/admin/settings")]);
      setItems(a.data);
      setCats(b.data);
      setCurrency(c.data.settings.locale.currency);
    } catch {
      toast.error(t("toast.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const canSort = !f.q && f.vis === "all" && f.avail === "all" && f.tr === "all";
  const filtersActive = !canSort || f.cat !== "all";
  const groups = useMemo(() => cats.map((c) => ({ cat: c, items: items.filter((i) => i.category_id === c.id && matches(i, f, lang)).sort((a, b) => a.sort_order - b.sort_order) })).filter((g) => f.cat === "all" ? g.items.length || !filtersActive : g.cat.id === f.cat), [cats, items, f, lang, filtersActive]);

  const changed = () => refresh();

  const reorder = useCallback(async (ids) => {
    const order = new Map(ids.map((id, i) => [id, i]));
    setItems((prev) => prev.map((it) => (order.has(it.id) ? { ...it, sort_order: order.get(it.id) } : it)));
    try {
      await api.put("/admin/items/reorder", { ids });
      toast.success(t("toast.orderSaved"));
      refresh();
    } catch {
      toast.error(t("toast.saveFailed"));
      load();
    }
  }, [t, refresh, load]);

  const onToggle = useCallback(async (item, key, value) => {
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, [key]: value } : i)));
    try {
      await api.patch(`/admin/items/${item.id}`, { [key]: value });
      refresh();
    } catch {
      toast.error(t("toast.saveFailed"));
      load();
    }
  }, [t, refresh, load]);

  const onDuplicate = useCallback(async (item) => {
    try {
      await api.post(`/admin/items/${item.id}/duplicate`);
      toast.success(t("toast.duplicated"));
      await load();
      refresh();
    } catch (e) {
      toast.error(errorText(e, t("toast.saveFailed")));
    }
  }, [t, load, refresh]);

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/items/${deleting.id}`);
      setItems((prev) => prev.filter((i) => i.id !== deleting.id));
      toast.success(t("toast.deleted"));
      changed();
    } catch (e) {
      toast.error(errorText(e, t("toast.saveFailed")));
    }
    setDeleting(null);
  };

  const onEdit = useCallback((item) => setEditor({ key: Date.now(), item }), []);
  const closeEditor = () => {
    setEditor(null);
    if (params.get("new")) setParams({}, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="admin-items-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">{t("items.title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("items.subtitle")}</p>
        </div>
        <Button onClick={() => setEditor({ key: Date.now(), item: null })} disabled={!cats.length} className="rounded-full" data-testid="add-item-btn"><Plus className="h-4 w-4" />{t("items.add")}</Button>
      </div>
      <div className="flex flex-col gap-2 rounded-2xl border bg-card p-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative flex-1 sm:min-w-[220px]">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={f.q} onChange={(e) => setFilter("q", e.target.value)} placeholder={t("items.search")} className="ps-9" data-testid="items-search-input" />
        </div>
        <FilterSelect value={f.cat} onChange={(v) => setFilter("cat", v)} testid="filter-category" options={[["all", t("items.allCategories")], ...cats.map((c) => [c.id, txt(c, "name", lang)])]} />
        <FilterSelect value={f.vis} onChange={(v) => setFilter("vis", v)} testid="filter-visibility" options={[["all", t("items.allVisibility")], ["visible", t("items.visible")], ["hidden", t("items.hidden")]]} />
        <FilterSelect value={f.avail} onChange={(v) => setFilter("avail", v)} testid="filter-availability" options={[["all", t("items.allAvailability")], ["available", t("items.available")], ["unavailable", t("items.unavailable")]]} />
        <FilterSelect value={f.tr} onChange={(v) => setFilter("tr", v)} testid="filter-translation" options={[["all", t("items.allTranslations")], ["missing", t("items.missingAny")], ["missing_ar", t("items.missingAr")], ["missing_en", t("items.missingEn")]]} />
        {filtersActive && <Button variant="ghost" size="sm" onClick={() => setF({ q: "", cat: "all", vis: "all", avail: "all", tr: "all" })} data-testid="clear-filters-btn">{t("items.clearFilters")}</Button>}
      </div>
      <p className="text-xs text-muted-foreground" data-testid="sort-hint">{canSort ? t("items.dragHint") : t("items.dragDisabled")}</p>
      {!loading && !cats.length && <p className="rounded-2xl border border-dashed p-10 text-center text-muted-foreground" data-testid="items-no-categories">{t("items.noCategories")}</p>}
      {!loading && cats.length > 0 && !groups.some((g) => g.items.length) && filtersActive && <p className="rounded-2xl border border-dashed p-10 text-center text-muted-foreground" data-testid="items-empty">{t("items.empty")}</p>}
      {groups.map(({ cat, items: list }) => {
        const ids = list.map((i) => i.id);
        return (
          <section key={cat.id} className="space-y-2" data-testid={`admin-item-group-${cat.id}`}>
            <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-muted-foreground">
              {txt(cat, "name", lang)}<span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] normal-case">{t("items.count", { n: list.length })}</span>
              {!cat.is_visible && <span className="rounded-full bg-zinc-500/20 px-2 py-0.5 text-[11px] normal-case">{t("items.hidden")}</span>}
            </h2>
            {!list.length && <p className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">{t("items.emptyCat")}</p>}
            <SortableList ids={ids} onReorder={reorder}>
              <div className="space-y-2">
                {list.map((item, idx) => (
                  <SortableRow key={item.id} id={item.id} disabled={!canSort}>
                    {(handle) => (
                      <ItemRow item={item} handle={handle} canSort={canSort} isFirst={idx === 0} isLast={idx === list.length - 1} t={t} lang={lang} currency={currency}
                        onToggle={onToggle} onMove={(it, d) => { const next = moveId(ids, it.id, d); if (next) reorder(next); }} onEdit={onEdit} onDuplicate={onDuplicate} onDelete={setDeleting} />
                    )}
                  </SortableRow>
                ))}
              </div>
            </SortableList>
          </section>
        );
      })}
      {editor && (
        <ItemEditor key={editor.key} item={editor.item} categories={cats} defaultCategory={f.cat !== "all" ? f.cat : cats[0]?.id} t={t} lang={lang} currency={currency}
          onClose={closeEditor} onSaved={() => { closeEditor(); load(); refresh(); }} />
      )}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-item-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("items.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("items.deleteBody", { name: deleting ? txt(deleting, "title", lang) : "" })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel data-testid="delete-item-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90" data-testid="delete-item-confirm">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
